import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageAlert from "../../components/PageAlert/PageAlert";
import { resolveApiError } from "../../utils/apiError";
import {
  ARTICLE_LOCALES,
  formatArticleDate,
  getArticle,
  getArticleBlocks,
  getFeaturedMediaUrl,
  getLocaleContent,
  isBilingualBlockType,
  newlinesToBreaks,
  normalizePaginated,
} from "./articleApi";
import "./articles.scss";

const hasMarkup = (value = "") => /<[a-z][\s\S]*>/i.test(value) || /<br\s*\/?>/i.test(value) || /\n/.test(value);

const renderHtmlText = (Tag, className, content, { wrapQuotes = false } = {}) => {
  const html = newlinesToBreaks(content);
  const body = wrapQuotes ? `“${html}”` : html;
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: body }} />;
};

const ArticlePreview = () => {
  const navigate = useNavigate();
  const { id: articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(Boolean(articleId));
  const [error, setError] = useState("");

  const loadArticle = useCallback(async () => {
    if (!articleId) {
      setError("Article ID is missing.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const articleResponse = await getArticle(articleId);
      const blockResponse = await getArticleBlocks(articleId);
      const blockResult = normalizePaginated(blockResponse);
      const sourceBlocks = blockResult.items.length ? blockResult.items : articleResponse.blocks || [];
      const seen = new Set();
      const uniqueBlocks = sourceBlocks.filter((block) => {
        if (!block?.id) return true;
        const key = String(block.id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      setArticle(articleResponse);
      setBlocks(uniqueBlocks);
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setLoading(false);
    }
  }, [articleId, navigate]);

  useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  const renderLocaleContent = (block, locale) => {
    const data = block.data || {};
    const localeData = getLocaleContent(data, locale);
    const content = localeData.content || "";

    if (block.type === "heading") {
      if (!content.trim()) return null;
      const HeadingTag = data.level || "h2";
      if (hasMarkup(content)) {
        return renderHtmlText(HeadingTag, "article-preview__heading", content);
      }
      return <HeadingTag className="article-preview__heading">{content}</HeadingTag>;
    }

    if (block.type === "paragraph" || block.type === "text") {
      if (!content.trim()) return null;
      if (hasMarkup(content) || /<[a-z][\s\S]*>/i.test(content)) {
        return renderHtmlText("div", "article-preview__paragraph", content);
      }
      return <p className="article-preview__paragraph">{content}</p>;
    }

    if (block.type === "quote") {
      if (!content.trim() && !localeData.author) return null;
      return (
        <blockquote className="article-preview__quote">
          {hasMarkup(content) ? (
            renderHtmlText("span", null, content, { wrapQuotes: true })
          ) : (
            <>“{content}”</>
          )}
          {localeData.author && <span>— {localeData.author}</span>}
        </blockquote>
      );
    }

    if (block.type === "button") {
      if (!content.trim() && !localeData.url) return null;
      return (
        <p>
          <a
            className="article-preview__button"
            href={localeData.url || "#"}
            target={data.target || "_self"}
            rel="noreferrer"
          >
            {content || "Read More"}
          </a>
        </p>
      );
    }

    if (block.type === "html") {
      if (!content.trim()) return null;
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }

    return null;
  };

  const renderBlock = (block, index) => {
    const data = block.data || {};

    if (block.type === "image") {
      return (
        <figure key={index} className="article-preview__image">
          {data.url && <img src={data.preview_url || data.url} alt={data.alt || ""} />}
          {data.caption && <figcaption>{data.caption}</figcaption>}
        </figure>
      );
    }

    if (block.type === "divider") {
      return <hr key={index} className="article-preview__divider" />;
    }

    if (block.type === "video" && data.url) {
      return (
        <div key={index} className="article-preview__video">
          <video controls>
            <source src={data.url} />
          </video>
        </div>
      );
    }

    if (block.type === "embed") {
      return <div key={index} dangerouslySetInnerHTML={{ __html: data.embed_code || "" }} />;
    }

    if (block.type === "table" && data.content) {
      return (
        <div
          key={index}
          className="article-preview__table"
          dangerouslySetInnerHTML={{ __html: data.content }}
        />
      );
    }

    if (block.type === "columns") {
      return (
        <div key={index} className="article-preview__columns">
          {(data.columns || []).map((column, columnIndex) => (
            <div key={columnIndex}>
              {(column.blocks || []).map((nested, nestedIndex) =>
                renderBlock(nested, `${index}-${columnIndex}-${nestedIndex}`)
              )}
            </div>
          ))}
        </div>
      );
    }

    if (isBilingualBlockType(block.type) || block.type === "text") {
      return (
        <React.Fragment key={index}>
          {ARTICLE_LOCALES.map((locale) => {
            const inner = renderLocaleContent(block, locale.value);
            if (!inner) return null;
            return <React.Fragment key={locale.value}>{inner}</React.Fragment>;
          })}
        </React.Fragment>
      );
    }

    return null;
  };

  if (loading) {
    return <div className="articles-page"><div className="articles-page__loading">Loading preview...</div></div>;
  }

  const featuredUrl = getFeaturedMediaUrl(article);

  return (
    <div className="articles-page">
      {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}
      <div className="articles-page__heading">
        <div>
          <h2>Article preview</h2>
          <p>Preview how the article appears before publishing.</p>
        </div>
        <div className="articles-page__heading-actions">
          <button className="ui-button" type="button" onClick={() => navigate(-1)}>Back</button>
        </div>
      </div>

      {article && (
        <div className="article-preview">
          <div className="article-preview__hero">
            <div>
              <span className={`articles-page__status articles-page__status--${article.status}`}>
                {article.status}
              </span>
              <h1>{article.title}</h1>
              <p>{article.excerpt}</p>
              <small>{formatArticleDate(article.created_at, { bilingual: true }) || "—"}</small>
            </div>
            {featuredUrl && (
              <img src={featuredUrl} alt={article.title || ""} />
            )}
          </div>
          <div className="article-preview__content">{blocks.map(renderBlock)}</div>
        </div>
      )}
    </div>
  );
};

export default ArticlePreview;
